//
// Current is global state that is needed for all pages. It's provided by the app
// and used to observe flash, detect login, etc.
//

import type { Cargo } from "@/vendor/types";
import type { Page } from "@inertiajs/core";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

const breakpoints = useBreakpoints(breakpointsTailwind);

export type AlertmousePage = Page<{ cargo: Cargo }>;

export default class Current {
  static singleton: Current;

  // reactive stuff based on the current page
  readonly cargo: Cargo = reactive({} as Cargo);
  readonly component = ref<string>("");

  // reactive stuff based on the current screen
  readonly xs = breakpoints.smaller("sm");
  readonly sm = breakpoints.greaterOrEqual("sm");
  readonly lg = breakpoints.greaterOrEqual("lg");

  //
  // update current on each inertia page load
  //

  update(page: AlertmousePage) {
    // log
    if (import.meta.env.MODE === "development") {
      this.log(page);
    }

    // update our state
    Object.assign(this.cargo, page.props.cargo);
    this.component.value = page.component;
  }

  //
  // handy accessors
  //

  get firstName(): string | undefined {
    return this.cargo.user?.name.split(" ")[0];
  }

  get loggedIn(): boolean {
    return !!this.cargo.user;
  }

  get loggedOut(): boolean {
    return !this.cargo.user;
  }

  get isOwner(): boolean {
    return this.cargo.role === "owner";
  }

  get team(): Team {
    if (!this.cargo.team) throw new Error("Current.team only works when logged in");
    return this.cargo.team;
  }

  get user(): User {
    if (!this.cargo.user) throw new Error("Current.user only works when logged in");
    return this.cargo.user;
  }

  log(page: AlertmousePage) {
    const print = JSON.stringify(page.props);
    if (print === this.#lastLog) return;
    this.#lastLog = print;
    console.log("🔥 %c%s", "color: limegreen", page.component, JSON.parse(print));
  }
  #lastLog = "";
}
Current.singleton = new Current();

//
// current.update when page changes
//

const page: AlertmousePage = usePage<AlertmousePage["props"]>();
watch([() => page.component, () => page.props], () => Current.singleton.update(page));

//
// handle server-side flash
//

watch(
  () => Current.singleton.cargo.flash,
  (flash) => {
    if (flash?.alert || flash?.notice) Util.flash(flash);
  },
  { immediate: true },
);
